/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import React from "react";
import { segnalaErrore } from "../lib/segnalaErrori";

/**
 * ErrorBoundary per le rotte lazy.
 *
 * Due scopi:
 *  1. ChunkLoadError — dopo un deploy i chunk hashati cambiano nome; un client
 *     con bundle vecchio in cache prova a caricare un chunk non più esistente →
 *     errore. In quel caso ricarichiamo la pagina UNA volta (guard in
 *     sessionStorage per evitare loop) così il browser prende il bundle nuovo.
 *  2. Qualsiasi altro errore di render di una pagina → invece di schermo nero
 *     (root smontata) mostriamo un fallback con pulsante "Ricarica".
 *  3. Dal 2026-09-18 l'errore viene anche SEGNALATO al server (`POST /api/errori`).
 *     Prima si scopriva solo se un operatore lo raccontava, e in campo non lo
 *     racconta nessuno: si rinuncia alla funzione e si tira avanti.
 *
 * I ChunkLoadError NON si segnalano: non sono guasti del codice ma il normale
 * effetto di un deploy su una scheda rimasta aperta, e riempirebbero l'elenco
 * di righe su cui non c'e' niente da correggere.
 */
const isChunkError = (error) => {
  const name = error && error.name;
  const msg = (error && error.message) || "";
  return name === "ChunkLoadError" || /Loading chunk [\d]+ failed/i.test(msg) || /dynamically imported module/i.test(msg);
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (!isChunkError(error)) {
      // Non attende e non puo' fallire: segnalaErrore inghiotte tutto.
      segnalaErrore(error, "render");
    }
    if (isChunkError(error)) {
      // Bundle stale dopo deploy: ricarica una volta sola
      const KEY = "tas_chunk_reloaded";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    // Pulisci la guard così un eventuale nuovo ChunkLoadError può ricaricare di nuovo
    try { sessionStorage.removeItem("tas_chunk_reloaded"); } catch (_) {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (isChunkError(this.state.error)) {
        // Il reload è già in corso (o già fatto): mostra messaggio neutro
        return (
          <div className="page"><div style={{ padding: "3rem", textAlign: "center", color: "#94a3b8" }}>
            Aggiornamento in corso…
          </div></div>
        );
      }
      return (
        <div className="page">
          <div style={{ padding: "3rem", textAlign: "center", color: "#cbd5e1" }}>
            <h2 style={{ color: "#f8fafc", marginBottom: "0.75rem" }}>Si è verificato un errore</h2>
            <p style={{ marginBottom: "1.25rem", fontSize: 14 }}>
              La pagina non è stata caricata correttamente. Il problema è stato
              segnalato automaticamente: i tuoi dati non sono stati toccati.
            </p>
            <button className="btn btn-primary" onClick={this.handleReload}>Ricarica</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
